import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, map, min, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {RowPriceService} from './API/row-price.service';
import {
    DEFAULT_SELECTED_LABELS,
    UI_LABEL_TO_BACKEND_KEY_MAP,
    DEFAULT_SELECTED_REGIONS, NAME_TO_SLUG_MAP, SLUG_TO_NAME_MAP
} from '../../ProductSearch/ListConfiguration/InfoConverstion';
import {getAvailablePort} from '@angular-devkit/build-angular/src/builders/ssr-dev-server/utils';

@Injectable({
    providedIn: 'root'
})
export class ConfiguratorStateService {

    private readonly PRICE_PER_LINE_IN_EUR:number = 0.1;
    private readonly TEST_CONFIG = {
        selectedTable: 'autohaendler',
        selectedStates: [
            'Baden-Württemberg',
            'Bayern',
            'Berlin',
            'Brandenburg',
            'Niedersachsen',
            'Nordrhein-Westfalen',
            'Rheinland-Pfalz',
            'Saarland'
        ],
        selectedCompanyInfos: ['Anschrift', 'Website', 'Google Rating', 'Email'],
        selectedQuantity: 5000,
        maxQuantity: 6474,
        selectedPrice: 500
    };

    private readonly minimumSelectedQuantityValue: number = 1000;
    private _selectedTable = new BehaviorSubject<string>('');
    private _selectedStates = new BehaviorSubject<string[]>(DEFAULT_SELECTED_REGIONS);
    private _selectedCompanyInfos = new BehaviorSubject<string[]>(DEFAULT_SELECTED_LABELS);
    private _selectedQuantity = new BehaviorSubject<number>(0);
    private _availableRowCount = new BehaviorSubject<number>(0);
    private _selectedPrice = new BehaviorSubject<number>(0);
    private _selectedTableDisplayName = new BehaviorSubject<string>('');

    // Observables for other components if needed
    selectedTable$ = this._selectedTable.asObservable();
    selectedStates$ = this._selectedStates.asObservable();
    selectedCompanyInfos$ = this._selectedCompanyInfos.asObservable();
    selectedQuantity$ = this._selectedQuantity.asObservable();
    maxQuantity$ = this._availableRowCount.asObservable();
    selectedPrice$ = this._selectedPrice.asObservable();
    selectedTableDisplayName$ = this._selectedTableDisplayName.asObservable();

    // Combine all state into one config object observable
    config$ = combineLatest([
        this.selectedTable$,
        this.selectedStates$,
        this.selectedCompanyInfos$,
        this.selectedQuantity$,
        this.maxQuantity$,
        this.selectedPrice$,
        this.selectedTableDisplayName$
    ]).pipe(
        map(
            ([
                 selectedTable,
                 selectedStates,
                 selectedCompanyInfos,
                 selectedQuantity,
                 maxQuantity,
                 selectedPrice,
                 selectedTableDisplayName
             ]) => ({
                selectedTable,
                selectedStates,
                selectedCompanyInfos,
                selectedQuantity,
                maxQuantity,
                selectedPrice,
                selectedTableDisplayName
            })
        )
    );

    constructor(private rowPriceService: RowPriceService) {
        // Log the full config object whenever any part changes
        this.config$.subscribe(config => {
            console.log('Current Config:', config);
        });

        // TEMP: pre-load fixture so other components work without manual selection
        // this.loadConfig(this.TEST_CONFIG);
    }

    fetchTableUpdateRowInfos(table: string): void {
        const displayName = SLUG_TO_NAME_MAP[table];
        this._selectedTableDisplayName.next(displayName);
        this._selectedTable.next(table);
        this.getRowCountAndPrice(this.selectedTable, this.selectedStates, this.selectedCompanyInfos, 0);
    }

    setStates(states: string[]) {
        this._selectedStates.next(states);
        this.getRowCountAndPrice(this.selectedTable, this.selectedStates, this.selectedCompanyInfos, 0);
        this.setSelectedQuantity(this.availableRowCount);
    }

    fetchUpdatedCompanyInfos(companyInfos: string[]) {
        this._selectedCompanyInfos.next(companyInfos);
        this.getRowCountAndPrice(this.selectedTable, this.selectedStates, this.selectedCompanyInfos, 0);
    }

    setAvailableRowCount(quantity: number) {
        this._availableRowCount.next(quantity);
    }

    setSelectedQuantity(quantity: number) {
        this._selectedQuantity.next(quantity);
    }

    setPrice(price: number) { // <== new setter
        this._selectedPrice.next(price);
    }

    // Getters (for synchronous access, e.g. in ngOnInit)
    get selectedTable() {
        return this._selectedTable.value;
    }

    get selectedTableDisplayName(): string {
        return this._selectedTableDisplayName.value;
    }

    get selectedStates() {
        return this._selectedStates.value;
    }

    get selectedCompanyInfos() {
        return this._selectedCompanyInfos.value;
    }

    get minimumSelectedQuantity() {
        return this.minimumSelectedQuantityValue;
    }

    get selectedQuantity() {
        return this._selectedQuantity.value;
    }

    get displayQuantity() {
        return Math.min(this.selectedQuantity, this.availableRowCount);
    }

    get availableRowCount() {
        return this._availableRowCount.value;
    }

    get selectedPrice() { // <== new getter
        return this._selectedPrice.value;
    }

    getRowCountAndPriceFake(tableName: string, states: string[] | null, columnLabels: string[] | null, max: number):void {
        if (max == 0) {
            return;
        }
        this.setSelectedQuantity(max);
        this.setPrice(max * this.PRICE_PER_LINE_IN_EUR);
    }

    getRowCountAndPrice(tableName: string, states: string[] | null, columnLabels: string[] | null, max: number): void {
        const properties = ConfiguratorStateService.translateLabelsToProps(columnLabels);

        this.rowPriceService
            .getRowCountAndPrice(tableName, states ?? [], properties, max)
            .pipe(
                tap((result: { rows: number, price: number }) => {
                    if (max == 0) {
                        this.setAvailableRowCount(result.rows);
                    }
                    this.setSelectedQuantity(result.rows);
                    this.setPrice(result.price);

                    if (result.rows > this.selectedQuantity) {
                        this.setSelectedQuantity(this.availableRowCount);
                    }
                    console.log(`Updated rows: ${result.rows}, price: ${result.price}`);
                }),
                catchError(err => {
                    console.error(`Error fetching counts for ${states}:`, err);
                    this.setPrice(0);
                    return of({rows: 0, price: 0});
                })
            )
            .subscribe();
    }

    updateQuantityPrice(quantity: number) {
        console.log('Update quantity price:', quantity);
        this.getRowCountAndPriceFake(this.selectedTable, this.selectedStates, this.selectedCompanyInfos, quantity);
    }

    // TESTING CONFIG KEEP
    loadConfig(config: {
        selectedTable: string;
        selectedStates: string[];
        selectedCompanyInfos: string[];
        selectedQuantity: number;
        maxQuantity: number;
        selectedPrice: number;
    }): void {
        this._selectedTable.next(config.selectedTable);
        this._selectedTableDisplayName.next(
            ConfiguratorStateService.reverseTableName(config.selectedTable)
        );
        this._selectedStates.next(config.selectedStates);
        this._selectedCompanyInfos.next(config.selectedCompanyInfos);
        this._availableRowCount.next(config.maxQuantity);
        this._selectedQuantity.next(
            Math.min(config.selectedQuantity, config.maxQuantity)
        );
        this._selectedPrice.next(config.selectedPrice);
    }


    private static translateLabelsToProps(labels: string[] | null | undefined): string[] {
        if (!labels) {
            return [];
        }
        return labels
            .map(label => UI_LABEL_TO_BACKEND_KEY_MAP[label])
            .filter((prop): prop is string => !!prop);
    }

    /** Reverse the slug produced by `generate_table_name` back to a readable label. */
    private static reverseTableName(slug: string): string {
        if (!slug) {
            return '';
        }

        let txt = slug
            .replace(/_/g, ' ')   // underscores -> spaces
            // German umlaut recovery
            .replace(/ae/g, 'ä')
            .replace(/oe/g, 'ö')
            .replace(/ue/g, 'ü')
            .replace(/e\b/g, 'é'); // crude recovery of final 'e' → 'é' (opt-in)

        // Capitalise first letter of every word
        txt = txt.replace(/\b\w/g, c => c.toUpperCase());

        return txt;
    }


}
